<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';

import { useResendCooldown } from './useResendCooldown';

defineOptions({ name: 'UiResendButton' });

interface Props {
  active?: boolean;
  countdownLabel?: string;
  cooldownSeconds?: number;
  disabled?: boolean;
  label?: string;
  resetKey?: number | string;
  restartOnClick?: boolean;
}

const {
  active = true,
  cooldownSeconds = 20,
  disabled = false,
  countdownLabel,
  label,
  resetKey = 0,
  restartOnClick = true,
} = defineProps<Props>();

const { t } = useTranslation();

const emit = defineEmits<{
  resend: [];
}>();

const { secondsRemaining, isCooldownActive, restartCooldown } = useResendCooldown({
  active: () => active,
  cooldownSeconds: () => cooldownSeconds,
  resetKey: () => resetKey,
});

const isDisabled = computed(() => {
  return disabled || isCooldownActive.value;
});

const buttonLabel = computed(() => {
  if (secondsRemaining.value === 0) {
    return label ?? t('ui.resend_button.resend');
  }

  return (countdownLabel ?? t('ui.resend_button.resend_countdown', { seconds: '{{seconds}}' }))
    .replace('{{seconds}}', String(secondsRemaining.value))
    .replace('{seconds}', String(secondsRemaining.value));
});

const handleClick = () => {
  if (isDisabled.value) {
    return;
  }

  emit('resend');

  if (restartOnClick) {
    restartCooldown();
  }
};
</script>

<template>
  <Button :label="buttonLabel" :disabled="isDisabled" @click="handleClick" />
</template>
